import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import { Email as EmailIcon, Send as SendIcon } from '@mui/icons-material';
import { authService } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { validateEmail } from '../utils/validation';

const Login: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const navigate = useNavigate();

  const validateEmailField = (email: string): boolean => {
    const error = validateEmail(email);
    setEmailError(error);
    return error === '';
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    if (emailError) {
      validateEmailField(value);
    }
  };

  const handleSendCode = async () => {
    if (!validateEmailField(email)) {
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await authService.login({ email });
      
      if (response.succeeded) {
        setSuccess('Verification code sent successfully! Please check your email.');
        // Navigate to PIN verification page after a short delay
        setTimeout(() => {
          navigate('/verify-pin', { state: { email } });
        }, 1500);
      } else {
        setError(response.errorDescription || 'Failed to send verification code');
      }
    } catch (error) {
      setError('Network error. Please try again.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            width: '100%',
            maxWidth: 400,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <EmailIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter your email to receive a verification code
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={() => validateEmailField(email)}
            error={!!emailError}
            helperText={emailError}
            placeholder="Enter your email address"
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSendCode}
            disabled={isLoading || !email || !!emailError}
            startIcon={isLoading ? <CircularProgress size={20} /> : <SendIcon />}
            sx={{ py: 1.5 }}
          >
            {isLoading ? 'Sending Code...' : 'Send Verification Code'}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            We'll send a 6-digit verification code to your email address
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 